import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import PortalLogo from '@/modules/idibu/components/PortalLogo';
import {
    POPULAR_ATS_PROVIDERS,
    ATS_REGIONS,
    type AtsProvider,
    type AtsRegion,
} from '../data/ats-providers';

interface AtsMarketplaceProps {
    /** Optional click handler — currently unused (read-only demo). */
    onProviderClick?: (provider: AtsProvider) => void;
}

export default function AtsMarketplace({ onProviderClick }: AtsMarketplaceProps) {
    const [search, setSearch] = useState('');
    const [region, setRegion] = useState<AtsRegion | ''>('');

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return POPULAR_ATS_PROVIDERS.filter((p) => {
            if (term && !p.name.toLowerCase().includes(term)) return false;
            if (region && p.region !== region) return false;
            return true;
        });
    }, [search, region]);

    return (
        <>
            <style>{MARKETPLACE_CSS}</style>
            <section className="km">
                <header className="km__header">
                    <div>
                        <h3>Available ATS integrations</h3>
                        <p>Sync candidates, roles, and pipeline stages from any of these providers.</p>
                    </div>
                </header>

                <div className="km__filters">
                    <label className="km__search">
                        <Search size={14} aria-hidden />
                        <input
                            type="text"
                            placeholder="Search providers…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search ATS providers"
                        />
                    </label>
                    <select
                        value={region}
                        onChange={(e) => setRegion((e.target.value as AtsRegion) || '')}
                        aria-label="Filter by region"
                    >
                        <option value="">All regions</option>
                        {ATS_REGIONS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </div>

                {filtered.length === 0 && (
                    <div className="km__empty">No providers match your filters.</div>
                )}

                <div className="km__grid">
                    {filtered.map((provider) => (
                        <ProviderCard
                            key={provider.id}
                            provider={provider}
                            onClick={onProviderClick ? () => onProviderClick(provider) : undefined}
                        />
                    ))}
                </div>
            </section>
        </>
    );
}

function ProviderCard({
    provider,
    onClick,
}: {
    provider: AtsProvider;
    onClick?: () => void;
}) {
    const interactive = Boolean(onClick);
    const Tag = interactive ? 'button' : 'div';

    return (
        <Tag
            type={interactive ? 'button' : undefined}
            onClick={onClick}
            className={`km-card ${interactive ? 'km-card--interactive' : ''}`}
        >
            <PortalLogo logo={provider.domain} name={provider.name} size={44} imageSize={32} />
            <span className="km-card__body">
                <span className="km-card__title-row">
                    <span className="km-card__title">{provider.name}</span>
                    {provider.popularRank <= 5 && (
                        <span className="km-card__pop">Popular</span>
                    )}
                </span>
                <span className="km-card__meta">
                    <span>{provider.region}</span>
                    <span aria-hidden> · </span>
                    <span className="km-card__desc">
                        {provider.description.length > 64
                            ? provider.description.slice(0, 64) + '…'
                            : provider.description}
                    </span>
                </span>
            </span>
        </Tag>
    );
}

const MARKETPLACE_CSS = `
.km {
    margin-top: 8px;
}

.km__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.km__header h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.005em;
}

.km__header p {
    margin: 3px 0 0;
    color: var(--text-tertiary);
    font-size: 12.5px;
}

.km__filters {
    display: grid;
    grid-template-columns: minmax(200px, 1fr) auto;
    gap: 8px;
    margin-bottom: 14px;
}

.km__search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-tertiary);
    transition: border-color 100ms ease, box-shadow 100ms ease;
}

.km__search:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-subtle);
    color: var(--text-secondary);
}

.km__search input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--text-primary);
    font-size: 13px;
    font-family: inherit;
}

.km__search input::placeholder { color: var(--text-tertiary); }

.km__filters select {
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 13px;
    outline: none;
    cursor: pointer;
    transition: border-color 100ms ease;
}

.km__filters select:hover {
    border-color: var(--border-emphasis);
    color: var(--text-primary);
}

.km__filters select:focus { border-color: var(--accent); }

.km__empty {
    padding: 32px;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 13px;
}

.km__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
}

.km-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    text-align: left;
    width: 100%;
    transition: background 100ms ease, border-color 100ms ease;
}

.km-card--interactive {
    cursor: pointer;
}

.km-card--interactive:hover {
    border-color: var(--border-emphasis);
    background: var(--surface-hover);
}

.km-card__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.km-card__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.km-card__title {
    color: var(--text-primary);
    font-size: 13.5px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.km-card__pop {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--accent-subtle);
    color: var(--accent-strong);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.km-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    color: var(--text-tertiary);
    font-size: 12px;
    line-height: 1.4;
    overflow: hidden;
}

.km-card__desc {
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
`;
